import { FlashIcon, CoinIcon, TrophyStarIcon } from '@repo/ui'
import { formatNumber } from 'src/utils/formatNumber'
import type { InventoryItem } from 'src/features/inventory/types/domain'
import { isBooster, isBonus } from 'src/features/inventory/types/domain'
import type { FreeBetGroup } from 'src/features/inventory/utils/groupFreeBets'
import { freeBetBannerCopy } from 'src/features/inventory/utils/freeBetBannerCopy'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { displayAmountClean } from 'src/utils/displayAmountClean'
import type { InventoryNotificationItem, LevelUpNotificationItem } from '../types/domain'

export const inventoryItemToNotification = (
  item: InventoryItem,
  t: (key: string) => string,
  claimItem?: (itemId: string) => Promise<void>
): InventoryNotificationItem => {
  if (isBooster(item)) {
    const boostValue = item.boost.value
    const boostUnit = item.boost.unit
    const formattedBoost = boostUnit === 'percentage' ? `${boostValue}%` : formatNumber(boostValue)

    return {
      type: 'inventory',
      icon: <FlashIcon className="w-[22px] h-[22px]" />,
      title: t('New Booster!'),
      subtitle: t('Inventory'),
      accentColor: 'blue',
      data: {
        reward: formattedBoost,
        tagLabel: t('Booster'),
        tagIcon: <FlashIcon className="w-[14px] h-[14px]" />
      }
    }
  }

  if (isBonus(item)) {
    return {
      type: 'inventory',
      icon: <CoinIcon className="w-[22px] h-[22px]" />,
      title: t('Item received'),
      subtitle: t('Inventory'),
      accentColor: 'green',
      data: {
        reward: `${getCurrencySymbol(item.currency)}${displayAmountClean(item.amount)}`,
        tagLabel: t('Free Bet'),
        tagIcon: <CoinIcon className="w-[14px] h-[14px]" />
      },
      ...(item.status === 'claimable' &&
        claimItem && {
          cta: {
            label: t('Claim'),
            onClick: (closeNotification) => {
              // Optimistically close notification immediately
              closeNotification()

              // Then claim the item in the background
              claimItem(item.id).catch((error) => {
                console.error('Claim failed:', error)
              })
            }
          }
        })
    }
  }

  // Fallback for unknown item types
  throw new Error(`Unknown inventory item type: ${(item as any).type}`)
}

/**
 * Builds a single informational banner for a whole free-bet grant (already
 * auto-claimed), e.g. "10 free bets on Plinko" with "$5 each". No CTA — the
 * bets are claimed automatically, so there is nothing for the player to do.
 */
export const freeBetGroupToNotification = (
  group: FreeBetGroup,
  t: (key: string, params?: Record<string, unknown>) => string,
  gameName?: string
): InventoryNotificationItem => {
  const copy = freeBetBannerCopy(group, t, gameName)

  return {
    type: 'inventory',
    icon: <CoinIcon className="w-[22px] h-[22px]" />,
    title: copy.title,
    subtitle: copy.subtitle,
    accentColor: 'green',
    count: copy.count,
    data: {
      reward: copy.reward,
      tagLabel: t('Free Bet'),
      tagIcon: <CoinIcon className="w-[14px] h-[14px]" />
    }
  }
}

/**
 * Creates a level-up notification item
 */
export const levelUpToNotification = (
  newLevel: number,
  multiplierBonus: number,
  t: (key: string) => string
): LevelUpNotificationItem => {
  return {
    type: 'levelup',
    icon: <TrophyStarIcon className="w-[22px] h-[22px]" />,
    title: t('Level Up!'),
    subtitle: t('Multiplier boost unlocked'),
    accentColor: 'yellow',
    data: {
      newLevel,
      multiplierBonus
    }
  }
}
